<template>
    <div class="roteirizacao-custo-panel">
        <div
            v-if="hasRoutes && (panelSubtitle || loading)"
            class="roteirizacao-custo-panel__subtitle-row mb-3"
        >
            <p
                v-if="panelSubtitle"
                class="roteirizacao-custo-panel__subtitle text-subtitle-1 font-weight-bold mb-0"
            >
                {{ panelSubtitle }}
            </p>
            <span v-if="loading" class="roteirizacao-custo-panel__sequencing text-caption">
                <v-progress-circular indeterminate size="16" width="2" color="primary" />
                {{ $t('routePlanning.drivers.loading') }}
            </span>
            <v-btn
                v-if="showViewControlContract"
                color="primary"
                dark
                small
                class="ml-2"
                @click.stop.prevent="onClickViewControlContract"
            >
                {{ $t('routePlanning.sidebar.viewContract') }}
            </v-btn>
        </div>
        <div v-if="showCycleFilter" class="mb-3">
            <RoteirizacaoWeekdayFilter v-model="selectedWeekdays" />
        </div>
        <RoteirizacaoCustoCicloFilter
            v-if="showCycleFilter"
            v-model="selectedCycleKey"
            :options="cycleFilterOptions"
        />
        <RoteirizacaoCollapsibleCard
            v-if="showCycleFilter && contractCycleTableRows.length"
            v-model="cycleContractsCardExpanded"
            class="mb-3"
            :title="$t('routePlanning.sidebar.costCycleContractsTitle')"
            variant="table"
            :expand-label="$t('routePlanning.tables.expand')"
            :collapse-label="$t('routePlanning.tables.collapse')"
        >
            <v-simple-table dense class="roteirizacao-custo-panel__table">
                <thead>
                    <tr>
                        <th>{{ $t('routePlanning.sidebar.costCycleColCode') }}</th>
                        <th>{{ $t('routePlanning.sidebar.costCycleColVehicleType') }}</th>
                        <th>{{ $t('routePlanning.sidebar.costCycleColTariff') }}</th>
                        <th class="text-right">{{ $t('routePlanning.sidebar.costCycleColValue') }}</th>
                        <th>{{ $t('routePlanning.sidebar.costCycleColCycleType') }}</th>
                        <th class="text-right">{{ $t('routePlanning.sidebar.costCycleColCycleDays') }}</th>
                        <th
                            v-if="canAccessContratos"
                            class="text-right"
                        >{{ $t('routePlanning.sidebar.costCycleColActions') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="row in contractCycleTableRows"
                        :key="row.key"
                    >
                        <td>{{ row.cod_contrato }}</td>
                        <td>{{ row.vehicleType }}</td>
                        <td>{{ row.descricao || '—' }}</td>
                        <td class="text-right">{{ formatMXNBRL(row.valor) }}</td>
                        <td>{{ formatCycleTipoLabel(row.cicloTipo, row.cicloDias) }}</td>
                        <td class="text-right">{{ row.cicloDias != null ? row.cicloDias : '—' }}</td>
                        <td
                            v-if="canAccessContratos"
                            class="text-right"
                        >
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                        icon
                                        x-small
                                        color="primary"
                                        v-bind="attrs"
                                        v-on="on"
                                        @click.stop.prevent="onClickViewOperationalContract(row.id_contrato)"
                                    >
                                        <v-icon small>mdi-eye</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ $t('routePlanning.sidebar.viewContract') }}</span>
                            </v-tooltip>
                        </td>
                    </tr>
                </tbody>
            </v-simple-table>
        </RoteirizacaoCollapsibleCard>
        <p v-if="!hasRoutes" class="roteirizacao-custo-panel__empty text-body-2 mb-0">
            {{ $t('routePlanning.sidebar.costEmpty') }}
        </p>
        <template v-else-if="summary && !isManualFleetMode">
            <div class="roteirizacao-custo-panel__totals mb-3">
                <div class="roteirizacao-custo-panel__total-row">
                    <span class="text-caption text--secondary">{{ costTotalLabel }}</span>
                    <span class="text-subtitle-1 font-weight-bold">{{ formatMXNBRL(displayedTotalCost) }}</span>
                </div>
                <div class="roteirizacao-custo-panel__total-row">
                    <span class="text-caption text--secondary">{{ $t('routePlanning.sidebar.costTotalKm') }}</span>
                    <span class="text-body-1">{{ formatKm(scaleKm(summary.totalMainKm)) }}</span>
                </div>
                <div class="roteirizacao-custo-panel__total-row">
                    <span class="text-caption text--secondary">{{ $t('routePlanning.sidebar.reusedVehiclesTotal') }}</span>
                    <span class="text-body-1">{{ reusedVehicleCount }}</span>
                </div>
                <div v-if="summary.laborTotal > 0" class="roteirizacao-custo-panel__total-row">
                    <span class="text-caption text--secondary">{{ $t('routePlanning.sidebar.costLabor') }}</span>
                    <span class="text-body-1">{{ formatMXNBRL(displayedLaborCost) }}</span>
                </div>
                <div v-if="summary.driverCount > 0" class="roteirizacao-custo-panel__total-row">
                    <span class="text-caption text--secondary">{{ $t('routePlanning.drivers.uniqueTotal') }}</span>
                    <span class="text-body-1">{{ summary.driverCount }}</span>
                </div>
                <div v-if="showOperationDaysInTotals" class="roteirizacao-custo-panel__total-row">
                    <span class="text-caption text--secondary">{{ operationDaysLabel }}</span>
                    <span class="text-body-1">{{ operationDaysResolved }}</span>
                </div>
                <div v-if="displayedOperationTaxesCost > 0" class="roteirizacao-custo-panel__total-row">
                    <span class="text-caption text--secondary">{{ operationTaxesLabel }} ({{ operationTaxesResolved }}%)</span>
                    <span class="text-body-1">{{ formatMXNBRL(displayedOperationTaxesCost) }}</span>
                </div>
            </div>
            <div v-if="typeRows.length" class="mb-3">
                <div class="text-caption font-weight-medium mb-1">{{ $t('routePlanning.sidebar.costByType') }}</div>
                <v-simple-table dense class="roteirizacao-custo-panel__table">
                    <thead>
                        <tr>
                            <th>{{ $t('routePlanning.sidebar.costTypeColumn') }}</th>
                            <th class="text-right">{{ $t('routePlanning.sidebar.costUnitsColumn') }}</th>
                            <th class="text-right">{{ $t('routePlanning.sidebar.costKmColumn') }}</th>
                            <th class="text-right">{{ costValueColumnLabel }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in typeRows" :key="row.type">
                            <td>{{ row.type }}</td>
                            <td class="text-right">{{ row.count }}</td>
                            <td class="text-right">{{ formatKm(scaleKm(row.km)).slice(0, -2) }}</td>
                            <td class="text-right">{{ formatMXNBRL(row.cost) }}</td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </div>
            <div v-if="driverByTypeRows.length" class="mb-3">
                <div class="text-caption font-weight-medium mb-1">{{ $t('routePlanning.drivers.costByTypeTitle') }}</div>
                <v-simple-table dense class="roteirizacao-custo-panel__table">
                    <thead>
                        <tr>
                            <th>{{ $t('routePlanning.sidebar.costTypeColumn') }}</th>
                            <th class="text-right">{{ $t('routePlanning.drivers.driversColumn') }}</th>
                            <th class="text-right">{{ $t('routePlanning.sidebar.laborTotalCostColumn') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in driverByTypeRows" :key="row.key">
                            <td>{{ row.typeLabel }}</td>
                            <td class="text-right">{{ row.drivers }}</td>
                            <td class="text-right">{{ formatMXNBRL(scaleCost(row.cost)) }}</td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </div>
            <div v-if="occupancyRows.length" class="mb-3">
                <div class="text-caption font-weight-medium mb-1">{{ $t('routePlanning.sidebar.costOccupancy') }}</div>
                <v-simple-table dense class="roteirizacao-custo-panel__table">
                    <thead>
                        <tr>
                            <th>{{ $t('routePlanning.sidebar.costVehicleColumn') }}</th>
                            <th class="text-right">{{ $t('routePlanning.routeTable.departureTime') }}</th>
                            <th class="text-right">{{ $t('routePlanning.routeTable.arrivalTime') }}</th>
                            <th class="text-right">{{ $t('routePlanning.sidebar.costTravelTime') }}</th>
                            <th class="text-right">{{ $t('routePlanning.sidebar.costKmColumn') }}</th>
                            <th class="text-right">{{ $t('routePlanning.fields.passengerAmount') }}</th>
                            <th class="text-right">{{ $t('routePlanning.sidebar.costPerSeat') }}</th>
                            <th class="text-right">{{ $t('routePlanning.sidebar.costPerPassenger') }}</th>
                            <th class="text-right">{{ $t('routePlanning.sidebar.costOccupancyColumn') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(oc, idx) in occupancyRows"
                            :key="`oc-${oc.vehicleId} - Rota ${idx + 1}`"
                        >
                            <td>{{ formatOccupancyVehicleLabel(oc, idx) }}</td>
                            <td class="text-right">{{ oc.departureTime }}</td>
                            <td class="text-right">{{ oc.arrivalTime }}</td>
                            <td class="text-right">{{ oc.travelTime }}</td>
                            <td class="text-right">{{ formatKm(scaleKm(oc.mainKm)).slice(0,-2) }}</td>
                            <td class="text-right">{{ oc.passengerLoad }} / {{ oc.capacity }}</td>
                            <td class="text-right">{{ formatCostPerSeat(oc) }}</td>
                            <td class="text-right">{{ formatCostPerPassenger(oc) }}</td>
                            <td class="text-right">{{ formatPercent(oc.ratio) }}</td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </div>
        </template>
        <template v-else-if="isManualFleetMode && (manualFleetDisplayRows.length || driverByTypeRows.length)">
            <div v-if="summary && summary.driverCount > 0" class="roteirizacao-custo-panel__total-row mb-3">
                <span class="text-caption text--secondary">{{ $t('routePlanning.drivers.uniqueTotal') }}</span>
                <span class="text-body-1">{{ summary.driverCount }}</span>
            </div>
            <div v-if="summary && summary.laborTotal > 0" class="roteirizacao-custo-panel__total-row mb-3">
                <span class="text-caption text--secondary">{{ $t('routePlanning.sidebar.costLabor') }}</span>
                <span class="text-body-1">{{ formatMXNBRL(scaleCost(summary.laborTotal)) }}</span>
            </div>
            <div v-if="driverByTypeRows.length" class="mb-3">
                <div class="text-caption font-weight-medium mb-1">{{ $t('routePlanning.drivers.costByTypeTitle') }}</div>
                <v-simple-table dense class="roteirizacao-custo-panel__table">
                    <thead>
                        <tr>
                            <th>{{ $t('routePlanning.sidebar.costTypeColumn') }}</th>
                            <th class="text-right">{{ $t('routePlanning.drivers.driversColumn') }}</th>
                            <th class="text-right">{{ $t('routePlanning.sidebar.laborTotalCostColumn') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in driverByTypeRows" :key="row.key">
                            <td>{{ row.typeLabel }}</td>
                            <td class="text-right">{{ row.drivers }}</td>
                            <td class="text-right">{{ formatMXNBRL(scaleCost(row.cost)) }}</td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </div>
            <div v-if="manualFleetDisplayRows.length" class="roteirizacao-custo-panel__total-row mb-3">
                <span class="text-caption text--secondary">{{ $t('routePlanning.sidebar.costTableDistance') }}</span>
                <span class="text-body-1">{{ manualDistanceLabel }}</span>
            </div>
            <div v-if="manualFleetDisplayRows.length" class="mb-3">
                <div class="text-caption font-weight-medium mb-1">{{ $t('routePlanning.sidebar.costOccupancy') }}</div>
                <v-simple-table dense class="roteirizacao-custo-panel__table">
                    <thead>
                        <tr>
                            <th>{{ $t('routePlanning.sidebar.costVehicleColumn') }}</th>
                            <th class="text-right">{{ $t('routePlanning.routeTable.departureTime') }}</th>
                            <th class="text-right">{{ $t('routePlanning.routeTable.arrivalTime') }}</th>
                            <th class="text-right">{{ $t('routePlanning.sidebar.costTravelTime') }}</th>
                            <th class="text-right">{{ $t('routePlanning.fields.passengerAmount') }}</th>
                            <th class="text-right">{{ $t('routePlanning.fields.capacity') }}</th>
                            <th class="text-right">{{ $t('routePlanning.sidebar.costOccupancyColumn') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(row, idx) in manualFleetDisplayRows" :key="`manual-fleet-${idx}`">
                            <td>{{ row.vehicleLabel }}</td>
                            <td class="text-right">{{ row.departureTime }}</td>
                            <td class="text-right">{{ row.arrivalTime }}</td>
                            <td class="text-right">{{ row.travelTime }}</td>
                            <td class="text-right">{{ row.passengersLabel }}</td>
                            <td class="text-right">{{ row.capacity }}</td>
                            <td class="text-right">{{ row.occupancyLabel }}</td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </div>
        </template>
        <template v-else>
            <div class="roteirizacao-custo-panel__total-row">
                <span class="text-caption text--secondary">{{ $t('routePlanning.sidebar.costTableDistance') }}</span>
                <span class="text-body-1">{{ manualDistanceLabel }}</span>
            </div>
        </template>
    </div>
</template>

<script>
import RoteirizacaoCustoCicloFilter from './RoteirizacaoCustoCicloFilter.vue';
import RoteirizacaoWeekdayFilter from './RoteirizacaoWeekdayFilter.vue';
import RoteirizacaoCollapsibleCard from './RoteirizacaoCollapsibleCard.vue';
import {
    todayRef,
    resolveCicloEfetivo,
    buildCicloFilterOptions,
    resolveSelectedDays,
    costScaleFactor,
    round2,
    WEEKDAY_KEYS,
} from '@/utils/roteirizacaoCicloPagamento';

export default {
    name: 'RoteirizacaoCustoPanel',
    components: {
        RoteirizacaoCustoCicloFilter,
        RoteirizacaoWeekdayFilter,
        RoteirizacaoCollapsibleCard,
    },
    props: {
        summary: {
            type: Object,
            default: null,
        },
        clientId: {
            type: [Number, String],
            default: null,
        },
        clients: {
            type: Array,
            default: () => [],
        },
        contractCycles: {
            type: Array,
            default: () => [],
        },
        contractCycleTableRows: {
            type: Array,
            default: () => [],
        },
        controlContract: {
            type: Object,
            default: null,
        },
        isOperationalMode: {
            type: Boolean,
            default: false,
        },
        isSimulationMode: {
            type: Boolean,
            default: false,
        },
        isPreviewMode: {
            type: Boolean,
            default: false,
        },
        canAccessContratos: {
            type: Boolean,
            default: false,
        },
        routingTable: {
            type: Array,
            default: () => [],
        },
        hasRoutes: {
            type: Boolean,
            default: false,
        },
        isManualFleetMode: {
            type: Boolean,
            default: false,
        },
        manualFleetRows: {
            type: Array,
            default: () => [],
        },
        pdfTitle: {
            type: String,
            default: '',
        },
        routingModeLabel: {
            type: String,
            default: '',
        },
        fleetModeLabel: {
            type: String,
            default: '',
        },
        operationDays: {
            type: [Number, String],
            default: 30,
        },
        operationTaxes: {
            type: [Number, String],
            default: 5,
        },
        loading: { type: Boolean, default: false }
    },
    data() {
        return {
            selectedCycleKey: null,
            /** Dias operacionais da semana (chaves i18n), começando na segunda. */
            selectedWeekdays: WEEKDAY_KEYS.slice(),
            cycleContractsCardExpanded: true,
        };
    },
    computed: {
        costTotalLabel() {
            return this.$t('routePlanning.sidebar.costTotal');
        },
        costValueColumnLabel() {
            return this.$t('routePlanning.sidebar.costValueColumn');
        },
        operationDaysLabel() {
            return this.$t('routePlanning.sidebar.operationDays');
        },
        operationDaysResolved() {
            const n = Number(this.operationDays);
            return Number.isFinite(n) && n > 0 ? n : 30;
        },
        operationTaxesLabel() {
            return this.$t('routePlanning.sidebar.operationTaxes');
        },
        operationTaxesResolved() {
            const n = Number(this.operationTaxes);
            return Number.isFinite(n) && n >= 0 ? n : 0;
        },
        /** Quantidade de dias da semana selecionados (1–7) para escala do período. */
        selectedWeekdayCount() {
            const selected = Array.isArray(this.selectedWeekdays) ? this.selectedWeekdays : [];
            const unique = [...new Set(selected.filter((k) => WEEKDAY_KEYS.includes(k)))];
            return unique.length > 0 ? unique.length : 7;
        },
        selectedWeekdaysLabel() {
            const selected = Array.isArray(this.selectedWeekdays) ? this.selectedWeekdays : [];
            const ordered = WEEKDAY_KEYS.filter((k) => selected.includes(k));
            if (!ordered.length || ordered.length === 7) {
                return this.$t('routePlanning.sidebar.costWeekdaysAll');
            }
            return ordered.map((k) => this.$t(`datetime.weekdays.${k}`)).join(', ');
        },
        /** Simulação/prévia: ciclo de referência = dias de operação informados. */
        usesOperationDaysCycle() {
            return (this.isSimulationMode || this.isPreviewMode) && !this.isManualFleetMode;
        },
        showOperationDaysInTotals() {
            return this.usesOperationDaysCycle && this.hasRoutes;
        },
        clientName() {
            if (this.clientId == null || this.clientId === '') return '';
            const client = (this.clients || []).find(
                (c) => c && String(c.id_cliente) === String(this.clientId),
            );
            return client && client.nome_cliente ? String(client.nome_cliente) : '';
        },
        controlLabel() {
            const c = this.controlContract;
            if (!c) return '';
            const code = c.cod != null ? String(c.cod).trim() : '';
            return `${code}`;
        },
        panelSubtitle() {
            if (this.isOperationalMode && this.hasRoutes && this.clientName && this.controlLabel) {
                return `${this.clientName} — ${this.controlLabel}`;
            }
            return this.clientName || '';
        },
        showViewControlContract() {
            return this.canAccessContratos
                && this.isOperationalMode
                && this.hasRoutes
                && this.controlContract
                && this.controlContract.id != null
                && this.controlContract.id !== '';
        },
        effectiveCycles() {
            if (!this.hasRoutes || this.isManualFleetMode) return [];
            if (this.isOperationalMode) {
                const diaRef = todayRef();
                return (this.contractCycles || [])
                    .map((row) => resolveCicloEfetivo(row, null, diaRef))
                    .filter((c) => c && Number.isFinite(Number(c.dias)) && Number(c.dias) > 0);
            }
            if (this.usesOperationDaysCycle) {
                return [{ dias: this.operationDaysResolved }];
            }
            return [];
        },
        cycleFilterBundle() {
            if (!this.effectiveCycles.length) {
                return { options: [], maxDias: 0, defaultKey: null };
            }
            const t = (key, values) => this.$t(key, values);
            return buildCicloFilterOptions(
                this.effectiveCycles,
                todayRef(),
                t,
                this.selectedWeekdayCount,
            );
        },
        cycleFilterOptions() {
            return this.cycleFilterBundle.options || [];
        },
        showCycleFilter() {
            return this.hasRoutes
                && !this.isManualFleetMode
                && this.cycleFilterOptions.length > 0
                && (this.isOperationalMode || this.usesOperationDaysCycle);
        },
        /** Dias do período de análise selecionado no filtro (ou null). */
        analysisPeriodDays() {
            if (!this.showCycleFilter) return null;
            return resolveSelectedDays(this.cycleFilterOptions, this.selectedCycleKey);
        },
        /**
         * Fator global: período / max(ciclo) dos contratos (operacional)
         * ou período / dias de operação (simulação/prévia).
         * Usado em mão de obra; ocupação usa o ciclo do tipo de veículo.
         */
        costScaleFactorValue() {
            if (!this.showCycleFilter) return 1;
            return costScaleFactor(this.analysisPeriodDays, this.cycleFilterBundle.maxDias);
        },
        /**
         * Tarifas fixa/variável agrupadas por tipo (id_tipo_veiculo ou rótulo).
         * role inferido do campo ou da key `id-role-contrato`.
         */
        contractRatesByTypeId() {
            const groups = Object.create(null);
            (this.contractCycleTableRows || []).forEach((row) => {
                if (!row) return;
                const role = this.resolveContractRowRole(row);
                const typeId = row.id_tipo_veiculo != null && row.id_tipo_veiculo !== ''
                    ? String(row.id_tipo_veiculo)
                    : (row.vehicleType != null ? String(row.vehicleType) : null);
                if (!typeId) return;
                if (!groups[typeId]) {
                    groups[typeId] = {
                        fixa: 0,
                        cicloFixa: null,
                        variavel: 0,
                        vehicleType: row.vehicleType != null ? String(row.vehicleType) : '',
                    };
                }
                const slot = groups[typeId];
                const valor = Number(row.valor);
                if (role === 'fixa' && Number.isFinite(valor)) {
                    slot.fixa = round2(valor);
                    slot.cicloFixa = Number.isFinite(Number(row.cicloDias))
                        ? Number(row.cicloDias)
                        : null;
                } else if (role === 'variavel' && Number.isFinite(valor)) {
                    slot.variavel = round2(valor);
                }
            });
            return groups;
        },
        /**
         * Mão de obra exibida na tela (já escalada pelo filtro quando aplicável).
         */
        displayedLaborCost() {
            const labor = this.summary && this.summary.laborTotal != null
                ? Number(this.summary.laborTotal)
                : 0;
            if (!Number.isFinite(labor) || labor <= 0) return 0;
            return this.scaleCost(labor);
        },
        /**
         * Custo total base com filtro de ciclo (sem impostos):
         * (distância do período × variável) + mão de obra da tela + (fixo × período/ciclo × qtd).
         */
        baseTotalCost() {
            const s = this.summary;
            if (!s) return null;
            if (!this.showCycleFilter) {
                return this.scaleCost(s.totalCost);
            }
            const period = this.analysisPeriodDays;
            if (!Number.isFinite(period) || period <= 0) {
                return this.scaleCost(s.totalCost);
            }

            // Simulação/prévia: fixo×(período/diasOperação) + variável×período + MO escalada.
            if (this.usesOperationDaysCycle) {
                return this.resolveOperationDaysTotalCost(s, period);
            }

            const groups = this.contractRatesByTypeId;
            const groupKeys = Object.keys(groups);
            if (!groupKeys.length) {
                return this.scaleCost(s.totalCost);
            }

            const laborPart = round2(this.displayedLaborCost);
            const totalKmPeriod = round2(this.scaleKm(Number(s.totalMainKm) || 0));
            const totalVehicles = this.resolveTotalVehicleCount();

            // Um tipo: distancia_total × variável + MO da tela + fixo × (período/ciclo) × qtd.
            // Km e tarifas arredondados a 2 casas — mesmos valores da prova real na tela.
            if (groupKeys.length === 1) {
                const g = groups[groupKeys[0]];
                const variablePart = round2(totalKmPeriod * (Number(g.variavel) || 0));
                const fixedPart = round2(
                    totalVehicles
                        * (Number(g.fixa) || 0)
                        * costScaleFactor(period, g.cicloFixa),
                );
                return round2(variablePart + laborPart + fixedPart);
            }

            // Vários tipos: usa custos já calculados em typeRows; se vazios, rateia km.
            let variablePart = 0;
            let fixedPart = 0;
            let matched = false;
            (this.typeRows || []).forEach((row) => {
                if ((Number(row.variableCostPeriod) || 0) > 0 || (Number(row.fixedCostPeriod) || 0) > 0) {
                    matched = true;
                }
                variablePart += Number(row.variableCostPeriod) || 0;
                fixedPart += Number(row.fixedCostPeriod) || 0;
            });
            if (!matched) {
                variablePart = 0;
                fixedPart = 0;
                const n = groupKeys.length;
                groupKeys.forEach((k) => {
                    const g = groups[k];
                    const shareKm = round2(totalKmPeriod / n);
                    const shareCount = Math.max(1, Math.round(totalVehicles / n));
                    variablePart += round2(shareKm * (Number(g.variavel) || 0));
                    fixedPart += round2(
                        shareCount
                            * (Number(g.fixa) || 0)
                            * costScaleFactor(period, g.cicloFixa),
                    );
                });
            }
            return round2(variablePart + laborPart + fixedPart);
        },
        /**
         * Imposto sobre operação calculado sobre o valor total base.
         */
        displayedOperationTaxesCost() {
            const base = this.baseTotalCost;
            if (!Number.isFinite(base) || base <= 0) return 0;
            const pct = this.operationTaxesResolved;
            if (!Number.isFinite(pct) || pct <= 0) return 0;
            return round2(base * (pct / 100));
        },
        /**
         * Custo total incluindo imposto sobre operação.
         */
        displayedTotalCost() {
            const base = this.baseTotalCost;
            if (!Number.isFinite(base)) return null;
            return round2(base + this.displayedOperationTaxesCost);
        },
        displayedTotals() {
            if (!this.hasRoutes || !this.summary || this.isManualFleetMode) return null;
            const cost = this.displayedTotalCost;
            if (!Number.isFinite(cost)) return null;
            return { cost, km: this.scaleKm(this.summary.totalMainKm) };
        },
        typeRows() {
            const s = this.summary;
            if (!s || !s.countByType) return [];
            const groups = this.contractRatesByTypeId;
            const period = this.analysisPeriodDays;
            const useContractPeriodFormula = this.showCycleFilter
                && this.isOperationalMode
                && Object.keys(groups).length > 0
                && Number.isFinite(period)
                && period > 0;
            const useOperationDaysFormula = this.showCycleFilter
                && this.usesOperationDaysCycle
                && Number.isFinite(period)
                && period > 0;
            const occupancySplitByType = useOperationDaysFormula
                ? this.buildOccupancyCostSplitByType()
                : null;
            const groupsByLabel = Object.create(null);
            Object.keys(groups).forEach((id) => {
                const g = groups[id];
                if (g.vehicleType) groupsByLabel[g.vehicleType] = g;
            });
            const cicloOperacao = this.operationDaysResolved;
            return Object.keys(s.countByType)
                .filter((key) => (s.countByType[key] || 0) > 0)
                .map((key) => {
                    const { type, capacity } = this.parseTypeAggKey(key);
                    const typeLabel = this.formatVehicleTypeLabel(type, capacity);
                    const count = s.countByType[key] || 0;
                    const km = (s.mainKmByType && s.mainKmByType[key]) || 0;
                    const baseCost = (s.costByType && s.costByType[key]) || 0;
                    if (useOperationDaysFormula) {
                        const split = (occupancySplitByType && occupancySplitByType[key])
                            || { fixed: 0, variable: 0 };
                        const fixedCostPeriod = round2(
                            (Number(split.fixed) || 0) * costScaleFactor(period, cicloOperacao),
                        );
                        const variableCostPeriod = round2((Number(split.variable) || 0) * period);
                        return {
                            type: typeLabel,
                            count,
                            km,
                            cost: round2(fixedCostPeriod + variableCostPeriod),
                            variableCostPeriod,
                            fixedCostPeriod,
                        };
                    }
                    const rates = groupsByLabel[typeLabel]
                        || this.findRatesForTypeLabel(typeLabel, groups)
                        || { fixa: 0, cicloFixa: null, variavel: 0 };
                    const kmPeriod = useContractPeriodFormula ? round2(this.scaleKm(km)) : km;
                    const variableCostPeriod = round2(kmPeriod * (Number(rates.variavel) || 0));
                    const fixedCostPeriod = useContractPeriodFormula
                        ? round2(
                            (Number(count) || 0)
                                * (Number(rates.fixa) || 0)
                                * costScaleFactor(period, rates.cicloFixa),
                        )
                        : 0;
                    const cost = useContractPeriodFormula
                        ? round2(variableCostPeriod + fixedCostPeriod)
                        : baseCost;
                    return {
                        type: typeLabel,
                        count,
                        km,
                        cost,
                        variableCostPeriod,
                        fixedCostPeriod,
                    };
                });
        },
        occupancyRows() {
            const rows = this.summary && Array.isArray(this.summary.perRouteOccupancy)
                ? this.summary.perRouteOccupancy
                : [];
            return rows.map((oc, idx) => ({
                ...oc,
                routeNumber: this.resolveOccupancyRouteNumber(oc, idx),
                routeCost: oc.routeCost,
                travelTime: this.formatTravelTime(oc.travelTimeSeconds),
                departureTime: this.formatScheduleTime(oc.departureTime),
                arrivalTime: this.formatScheduleTime(oc.arrivalTime),
            }));
        },
        /** Motoristas do sequenciamento por modal (o motorista não troca de modal). */
        driverByTypeRows() {
            const byType = this.summary && this.summary.driversByType && typeof this.summary.driversByType === 'object'
                ? this.summary.driversByType
                : {};
            return Object.keys(byType).sort().map((key) => {
                const row = byType[key] || {};
                const sep = key.lastIndexOf('|');
                const type = sep >= 0 ? key.slice(0, sep) : key;
                const capacity = sep >= 0 ? key.slice(sep + 1) : null;
                return {
                    key,
                    typeLabel: capacity != null ? this.formatVehicleTypeLabel(type, capacity) : type,
                    drivers: Number(row.motoristas) || 0,
                    cost: Number(row.custo) || 0,
                };
            });
        },
        manualFleetDisplayRows() {
            return (this.manualFleetRows || []).map((row, idx) => {
                const hasRoute = row.routeNumber != null;
                const typeLabel = this.formatVehicleTypeLabel(row.type, row.capacity);
                const vehicleLabel = hasRoute
                    ? `${typeLabel} - ${this.$t('routePlanning.routeTable.routeNumber')} ${row.routeNumber} - #${row.vehicleId}`
                    : (row.count != null
                        ? `${typeLabel} (${row.count}×)`
                        : typeLabel);
                const passengersLabel = row.passengerLoad != null
                    ? String(row.passengerLoad)
                    : '—';
                const occupancyLabel = row.ratio != null
                    ? this.formatPercent(row.ratio)
                    : '—';
                return {
                    vehicleLabel,
                    departureTime: this.formatScheduleTime(row.departureTime),
                    arrivalTime: this.formatScheduleTime(row.arrivalTime),
                    travelTime: row.travelTimeSeconds != null && row.travelTimeSeconds > 0
                        ? this.formatTravelTime(row.travelTimeSeconds)
                        : '—',
                    passengersLabel,
                    capacity: row.capacity,
                    occupancyLabel,
                    _idx: idx,
                };
            });
        },
        manualDistanceLabel() {
            const rows = Array.isArray(this.routingTable) ? this.routingTable : [];
            if (!rows.length) return '—';
            let sumKm = 0;
            rows.forEach((r) => {
                const raw = r && r.distance != null ? String(r.distance) : '';
                const n = parseFloat(raw.replace(',', '.'));
                if (Number.isFinite(n)) sumKm += n;
            });
            return sumKm > 0 ? `${sumKm.toFixed(2)} km` : '—';
        },
        reusedVehicleCount() {
            const rows = this.summary && Array.isArray(this.summary.perRouteOccupancy)
                ? this.summary.perRouteOccupancy
                : [];
            if (!rows.length) return 0;
            const tripCountByPhysical = new Map();
            for (const oc of rows) {
                const pid = oc.physicalVehicleId != null
                    ? Number(oc.physicalVehicleId)
                    : Number(oc.vehicleId);
                if (!Number.isFinite(pid)) continue;
                tripCountByPhysical.set(pid, (tripCountByPhysical.get(pid) || 0) + 1);
            }
            let reused = 0;
            for (const count of tripCountByPhysical.values()) {
                if (count > 1) reused += 1;
            }
            return reused;
        },
        pdfTitleResolved() {
            const explicit = String(this.pdfTitle || '').trim();
            if (explicit) return explicit;
            const base = this.$t('routePlanning.sidebar.costTitle');
            const routingMode = String(this.routingModeLabel || '').trim();
            const fleetMode = String(this.fleetModeLabel || '').trim();
            if (!routingMode && !fleetMode) return base;
            return this.$t('routePlanning.sidebar.costPdfTitleWithModes', {
                title: base,
                routingMode,
                fleetMode,
            });
        },
        pdfHeaders() {
            return [
                { text: this.$t('routePlanning.sidebar.costPdfField'), value: 'label' },
                { text: this.$t('routePlanning.sidebar.costPdfValue'), value: 'value' }];
        },
        pdfSections() {
            const twoColHeaders = this.pdfHeaders;
            if (!this.hasRoutes) {
                return [{
                    sectionTitle: '',
                    headers: twoColHeaders,
                    content: [{ label: this.$t('routePlanning.sidebar.costEmpty'), value: '—' }],
                }];
            }
            if (this.summary && !this.isManualFleetMode) {
                const sections = [];
                const totalsContent = [];
                if (this.showCycleFilter && this.cycleFilterOptions.length) {
                    const selectedOpt = (this.cycleFilterOptions || []).find(
                        (o) => o && String(o.value) === String(this.selectedCycleKey),
                    );
                    if (selectedOpt) {
                        sections.push({
                            sectionTitle: this.$t('routePlanning.sidebar.costCycleFilter'),
                            headers: twoColHeaders,
                            content: [
                                {
                                    label: this.$t('routePlanning.sidebar.costWeekdays'),
                                    value: this.selectedWeekdaysLabel,
                                },
                                {
                                    label: this.$t('routePlanning.sidebar.costCycleFilter'),
                                    value: selectedOpt.text != null
                                        ? String(selectedOpt.text)
                                        : String(selectedOpt.value),
                                },
                                {
                                    label: this.$t('routePlanning.sidebar.costCycleColCycleDays'),
                                    value: selectedOpt.dias != null
                                        ? String(selectedOpt.dias)
                                        : '—',
                                },
                            ],
                        });
                    }
                }
                if (this.panelSubtitle) {
                    totalsContent.push({
                        label: this.$t('routePlanning.sidebar.clientName'),
                        value: this.panelSubtitle,
                    });
                }
                totalsContent.push(
                    {
                        label: this.costTotalLabel,
                        value: this.formatMXNBRL(this.displayedTotalCost),
                    },
                    {
                        label: this.$t('routePlanning.sidebar.costTotalKm'),
                        value: this.formatKm(this.scaleKm(this.summary.totalMainKm)),
                    },
                    {
                        label: this.$t('routePlanning.sidebar.reusedVehiclesTotal'),
                        value: String(this.reusedVehicleCount),
                    },
                );
                if (Number(this.summary.laborTotal) > 0) {
                    totalsContent.push({
                        label: this.$t('routePlanning.sidebar.costLabor'),
                        value: this.formatMXNBRL(this.displayedLaborCost),
                    });
                }
                if (Number(this.summary.driverCount) > 0) {
                    totalsContent.push({
                        label: this.$t('routePlanning.drivers.uniqueTotal'),
                        value: String(this.summary.driverCount),
                    });
                }
                if (this.showOperationDaysInTotals) {
                    totalsContent.push({
                        label: this.operationDaysLabel,
                        value: String(this.operationDaysResolved),
                    });
                }
                if (this.displayedOperationTaxesCost > 0) {
                    totalsContent.push({
                        label: `${this.operationTaxesLabel} (${this.operationTaxesResolved}%)`,
                        value: this.formatMXNBRL(this.displayedOperationTaxesCost),
                    });
                }
                sections.push({
                    sectionTitle: this.$t('routePlanning.sidebar.costPdfSectionTotals'),
                    headers: twoColHeaders,
                    content: totalsContent,
                });
                if (this.showCycleFilter && this.contractCycleTableRows.length) {
                    sections.push({
                        sectionTitle: this.$t('routePlanning.sidebar.costCycleContractsTitle'),
                        headers: [
                            { text: this.$t('routePlanning.sidebar.costCycleColCode'), value: 'cod' },
                            { text: this.$t('routePlanning.sidebar.costCycleColVehicleType'), value: 'vehicleType' },
                            { text: this.$t('routePlanning.sidebar.costCycleColTariff'), value: 'tariff' },
                            { text: this.$t('routePlanning.sidebar.costCycleColValue'), value: 'valor' },
                            { text: this.$t('routePlanning.sidebar.costCycleColCycleType'), value: 'cicloTipo' },
                            { text: this.$t('routePlanning.sidebar.costCycleColCycleDays'), value: 'cicloDias' },
                        ],
                        content: this.contractCycleTableRows.map((row) => ({
                            cod: row.cod_contrato,
                            vehicleType: row.vehicleType,
                            tariff: row.descricao || '—',
                            valor: this.formatMXNBRL(row.valor),
                            cicloTipo: this.formatCycleTipoLabel(row.cicloTipo, row.cicloDias),
                            cicloDias: row.cicloDias != null ? String(row.cicloDias) : '—',
                        })),
                    });
                }
                if (this.typeRows.length) {
                    sections.push({
                        sectionTitle: this.$t('routePlanning.sidebar.costByType'),
                        headers: [
                            { text: this.$t('routePlanning.sidebar.costTypeColumn'), value: 'type' },
                            { text: this.$t('routePlanning.sidebar.costUnitsColumn'), value: 'count' },
                            { text: this.$t('routePlanning.sidebar.costKmColumn'), value: 'km' },
                            { text: this.costValueColumnLabel, value: 'cost' }],
                        content: this.typeRows.map((row) => ({
                            type: row.type,
                            count: String(row.count),
                            km: this.formatKm(this.scaleKm(row.km)).slice(0, -2),
                            cost: this.formatMXNBRL(row.cost),
                        })),
                    });
                    sections.push({
                        sectionTitle: this.$t('routePlanning.sidebar.costByTypeChartTitle'),
                        chart: this.buildCostBarsChart(
                            this.typeRows.map((row) => row.type),
                            this.typeRows.map((row) => Number(row.cost) || 0),
                            this.costValueColumnLabel,
                            '#1976d2',
                        ),
                    });
                }
                if (this.driverByTypeRows.length) {
                    sections.push({
                        sectionTitle: this.$t('routePlanning.drivers.costByTypeTitle'),
                        headers: [
                            { text: this.$t('routePlanning.sidebar.costTypeColumn'), value: 'typeLabel' },
                            { text: this.$t('routePlanning.drivers.driversColumn'), value: 'drivers' },
                            { text: this.$t('routePlanning.sidebar.laborTotalCostColumn'), value: 'cost' },
                        ],
                        content: this.driverByTypeRows.map((row) => ({
                            typeLabel: row.typeLabel,
                            drivers: String(row.drivers),
                            cost: this.formatMXNBRL(this.scaleCost(row.cost)),
                        })),
                    });
                    sections.push({
                        sectionTitle: this.$t('routePlanning.sidebar.laborByTypeChartTitle'),
                        chart: this.buildCostBarsChart(
                            this.driverByTypeRows.map((row) => row.typeLabel),
                            this.driverByTypeRows.map((row) => Number(this.scaleCost(row.cost)) || 0),
                            this.$t('routePlanning.sidebar.laborTotalCostColumn'),
                            '#90caf9',
                        ),
                    });
                }
                const occupancy = this.occupancyRows;
                if (occupancy.length) {
                    sections.push({
                        sectionTitle: this.$t('routePlanning.sidebar.costOccupancy'),
                        headers: [
                            { text: this.$t('routePlanning.sidebar.costVehicleColumn'), value: 'vehicle' },
                            { text: this.$t('routePlanning.routeTable.departureTime'), value: 'departureTime' },
                            { text: this.$t('routePlanning.routeTable.arrivalTime'), value: 'arrivalTime' },
                            { text: this.$t('routePlanning.sidebar.costTravelTime'), value: 'travelTime' },
                            { text: this.$t('routePlanning.sidebar.costKmColumn'), value: 'km' },
                            { text: this.$t('routePlanning.fields.passengerAmount'), value: 'passengers' },
                            { text: this.$t('routePlanning.sidebar.costPerSeat'), value: 'costPerSeat' },
                            { text: this.$t('routePlanning.sidebar.costPerPassenger'), value: 'costPerPassenger' },
                            { text: this.$t('routePlanning.sidebar.costOccupancyColumn'), value: 'occupancy' }],
                        content: occupancy.map((oc, idx) => ({
                            vehicle: this.formatOccupancyVehicleLabel(oc, idx),
                            departureTime: oc.departureTime,
                            arrivalTime: oc.arrivalTime,
                            travelTime: oc.travelTime,
                            km: this.formatKm(this.scaleKm(oc.mainKm)).slice(0, -2),
                            passengers: `${oc.passengerLoad} / ${oc.capacity}`,
                            costPerSeat: this.formatCostPerSeat(oc),
                            costPerPassenger: this.formatCostPerPassenger(oc),
                            occupancy: this.formatPercent(oc.ratio),
                        })),
                    });
                }
                return sections;
            }
            if (this.isManualFleetMode && this.manualFleetDisplayRows.length) {
                const sections = [{
                    sectionTitle: '',
                    headers: twoColHeaders,
                    content: [{
                        label: this.$t('routePlanning.sidebar.costTableDistance'),
                        value: this.manualDistanceLabel,
                    }],
                }];
                sections.push({
                    sectionTitle: this.$t('routePlanning.sidebar.costOccupancy'),
                    headers: [
                        { text: this.$t('routePlanning.sidebar.costVehicleColumn'), value: 'vehicle' },
                        { text: this.$t('routePlanning.routeTable.departureTime'), value: 'departureTime' },
                        { text: this.$t('routePlanning.routeTable.arrivalTime'), value: 'arrivalTime' },
                        { text: this.$t('routePlanning.sidebar.costTravelTime'), value: 'travelTime' },
                        { text: this.$t('routePlanning.fields.passengerAmount'), value: 'passengers' },
                        { text: this.$t('routePlanning.fields.capacity'), value: 'capacity' },
                        { text: this.$t('routePlanning.sidebar.costOccupancyColumn'), value: 'occupancy' }
                    ],
                    content: this.manualFleetDisplayRows.map((row) => ({
                        vehicle: row.vehicleLabel,
                        departureTime: row.departureTime,
                        arrivalTime: row.arrivalTime,
                        travelTime: row.travelTime,
                        passengers: row.passengersLabel,
                        capacity: String(row.capacity),
                        occupancy: row.occupancyLabel
                    })),
                });
                return sections;
            }
            return [{
                sectionTitle: '',
                headers: twoColHeaders,
                content: [{
                    label: this.$t('routePlanning.sidebar.costTableDistance'),
                    value: this.manualDistanceLabel,
                }],
            }];
        },
    },
    watch: {
        /** Totais como exibidos (período, imposto e mão de obra), para o consolidado multi-destino. */
        displayedTotals: {
            immediate: true,
            handler(val) {
                this.$emit('displayed-totals', val);
            },
        },
        selectedWeekdays(next) {
            if (!Array.isArray(next) || next.length === 0) {
                this.$nextTick(() => {
                    this.selectedWeekdays = [WEEKDAY_KEYS[0]];
                });
            }
        },
        'cycleFilterBundle.defaultKey': {
            immediate: true,
            handler(defaultKey) {
                const keys = new Set((this.cycleFilterOptions || []).map((o) => String(o.value)));
                if (!defaultKey) {
                    this.selectedCycleKey = null;
                    return;
                }
                if (this.selectedCycleKey == null || !keys.has(String(this.selectedCycleKey))) {
                    this.selectedCycleKey = defaultKey;
                }
            },
        },
    },
    methods: {
        resolveContractRowRole(row) {
            if (!row) return null;
            if (row.role === 'fixa' || row.role === 'variavel' || row.role === 'labor') {
                return row.role;
            }
            const key = row.key != null ? String(row.key) : '';
            if (key.includes('-fixa-')) return 'fixa';
            if (key.includes('-variavel-')) return 'variavel';
            if (key.includes('-labor-')) return 'labor';
            return null;
        },
        findRatesForTypeLabel(typeLabel, groups) {
            if (!typeLabel || !groups) return null;
            const needle = String(typeLabel).trim().toLowerCase();
            const keys = Object.keys(groups);
            for (let i = 0; i < keys.length; i += 1) {
                const g = groups[keys[i]];
                const label = g && g.vehicleType != null ? String(g.vehicleType).trim().toLowerCase() : '';
                if (label && label === needle) return g;
            }
            if (keys.length === 1) return groups[keys[0]];
            return null;
        },
        resolveTotalVehicleCount() {
            const s = this.summary;
            if (!s) return 1;
            if (Array.isArray(s.usedVehicleIds) && s.usedVehicleIds.length) {
                return s.usedVehicleIds.length;
            }
            const counts = Object.values(s.countByType || {})
                .map(Number)
                .filter((n) => Number.isFinite(n) && n > 0);
            if (counts.length) {
                return counts.reduce((acc, n) => acc + n, 0);
            }
            return Math.max(1, Number(this.reusedVehicleCount) || 1);
        },
        scaleCost(value) {
            const n = Number(value);
            if (!Number.isFinite(n)) return value;
            return n * this.costScaleFactorValue;
        },
        /**
         * Km diários × dias do período de análise (filtro). Sem filtro = 1 dia.
         */
        scaleKm(value) {
            const n = Number(value);
            if (!Number.isFinite(n)) return value;
            const days = this.analysisPeriodDays;
            const factor = Number.isFinite(days) && days > 0 ? days : 1;
            return n * factor;
        },
        parseTypeAggKey(key) {
            const text = String(key || '');
            const sep = text.lastIndexOf('|');
            if (sep < 0) {
                return { type: text || '—', capacity: 1 };
            }
            const type = text.slice(0, sep) || '—';
            const capacity = Number(text.slice(sep + 1));
            return {
                type,
                capacity: Number.isFinite(capacity) && capacity > 0 ? capacity : 1,
            };
        },
        formatVehicleTypeLabel(type, capacity) {
            const cap = Number(capacity);
            if (!type || type === '—') return '—';
            let str = '';
            if (type.includes(' - ')) {
                str = type.split(' - ')[0];
            } else {
                str = type;
            }
            return this.$t('routePlanning.routeTable.vehicleTypeLabel', {
                type: str,
                capacity: cap,
            });
        },
        resolveOccupancyRouteNumber(oc, idx) {
            if (oc && oc.routeNumber != null) return Number(oc.routeNumber);
            const sorted = [...(this.routingTable || [])]
                .filter((row) => row && row.vehicleType !== '-' && row.vehicleType != null)
                .sort((a, b) => {
                    const na = a.routeNumber != null ? Number(a.routeNumber) : Infinity;
                    const nb = b.routeNumber != null ? Number(b.routeNumber) : Infinity;
                    return na - nb;
                });
            if (sorted[idx] && sorted[idx].routeNumber != null) {
                return Number(sorted[idx].routeNumber);
            }
            return idx + 1;
        },
        formatOccupancyVehicleLabel(oc, idx) {
            const typeLabel = this.formatVehicleTypeLabel(oc.type, oc.capacity);
            const routeNum = oc.routeNumber != null ? oc.routeNumber : (idx + 1);
            return `${typeLabel} - ${this.$t('routePlanning.routeTable.routeNumber')} ${routeNum} - #${oc.vehicleId}`;
        },
        /**
         * Tarifas/ciclo do tipo de veículo da linha de ocupação (não o maxDias global/mensal).
         */
        resolveOccupancyTypeRates(oc) {
            const groups = this.contractRatesByTypeId || {};
            const keys = Object.keys(groups);
            if (!keys.length) return null;
            const typeLabel = this.formatVehicleTypeLabel(oc && oc.type, oc && oc.capacity);
            return this.findRatesForTypeLabel(typeLabel, groups) || (keys.length === 1 ? groups[keys[0]] : null);
        },
        /**
         * Custo unitário no período: fixo × (período/ciclo do tipo) + variável × dias do período.
         */
        scaleOccupancyUnitCost(fixedShare, variableShare, oc) {
            const fixed = Number(fixedShare) || 0;
            const variable = Number(variableShare) || 0;
            if (!this.showCycleFilter) {
                return fixed + variable;
            }
            const period = this.analysisPeriodDays;
            if (!Number.isFinite(period) || period <= 0) {
                return fixed + variable;
            }
            const rates = this.resolveOccupancyTypeRates(oc);
            let cicloFixa = rates && rates.cicloFixa != null ? rates.cicloFixa : null;
            if (cicloFixa == null && this.usesOperationDaysCycle) {
                cicloFixa = this.operationDaysResolved;
            }
            const fixedPeriod = fixed * costScaleFactor(period, cicloFixa);
            const variablePeriod = variable * period;
            return fixedPeriod + variablePeriod;
        },
        /**
         * Fixo/variável diários por chave tipo|capacidade (ocupação).
         */
        buildOccupancyCostSplitByType() {
            const map = Object.create(null);
            const rows = this.summary && Array.isArray(this.summary.perRouteOccupancy)
                ? this.summary.perRouteOccupancy
                : [];
            rows.forEach((oc) => {
                if (!oc) return;
                const type = oc.type != null ? String(oc.type) : '—';
                const capacity = Number.isFinite(Number(oc.capacity)) ? Number(oc.capacity) : 1;
                const key = `${type}|${capacity}`;
                if (!map[key]) map[key] = { fixed: 0, variable: 0 };
                map[key].fixed += Number(oc.fixedCost) || 0;
                map[key].variable += Number(oc.variableCost) || 0;
            });
            return map;
        },
        /**
         * Total simulação/prévia no período de análise.
         */
        resolveOperationDaysTotalCost(summary, period) {
            const ciclo = this.operationDaysResolved;
            const occ = summary && Array.isArray(summary.perRouteOccupancy)
                ? summary.perRouteOccupancy
                : [];
            if (!occ.length) {
                return this.scaleCost(summary.totalCost);
            }
            let fixedSum = 0;
            let variableSum = 0;
            occ.forEach((oc) => {
                fixedSum += Number(oc.fixedCost) || 0;
                variableSum += Number(oc.variableCost) || 0;
            });
            const laborPart = round2(this.displayedLaborCost);
            const fixedPart = round2(fixedSum * costScaleFactor(period, ciclo));
            const variablePart = round2(variableSum * period);
            return round2(fixedPart + variablePart + laborPart);
        },
        formatCostPerPassenger(oc) {
            const vehicleKey = oc && oc.vehicleId != null ? Number(oc.vehicleId) : null;
            if (vehicleKey == null) return '—';
            const sameVehicleRows = this.occupancyRows.filter(
                (o) => o.vehicleId != null && Number(o.vehicleId) === vehicleKey,
            );
            const countVehiclesbyId = sameVehicleRows.length;
            if (countVehiclesbyId <= 0) return '—';
            const fixedCost = sameVehicleRows.reduce(
                (sum, o) => sum + (Number(o.fixedCost) || 0),
                0,
            );
            const costByKm = Number(oc.variableCost);
            const load = Number(oc && oc.passengerLoad);
            if (!Number.isFinite(load) || load <= 0) return '—';
            if (!Number.isFinite(costByKm)) return '—';
            const periodUnit = this.scaleOccupancyUnitCost(
                fixedCost / countVehiclesbyId,
                costByKm,
                oc,
            );
            const costPerPassenger = periodUnit / load;
            if (!Number.isFinite(costPerPassenger)) return '—';
            return this.formatMXNBRL(round2(costPerPassenger));
        },
        formatCostPerSeat(oc) {
            const vehicleKey = oc && oc.vehicleId != null ? Number(oc.vehicleId) : null;
            if (vehicleKey == null) return '—';
            const sameVehicleRows = this.occupancyRows.filter(
                (o) => o.vehicleId != null && Number(o.vehicleId) === vehicleKey,
            );
            const countVehiclesbyId = sameVehicleRows.length;
            if (countVehiclesbyId <= 0) return '—';
            const fixedCost = sameVehicleRows.reduce(
                (sum, o) => sum + (Number(o.fixedCost) || 0),
                0,
            );
            const capacity = Number(oc && oc.capacity);
            if (!Number.isFinite(capacity) || capacity <= 0) return '—';
            const costByKm = Number(oc.variableCost);
            if (!Number.isFinite(costByKm)) return '—';
            const periodUnit = this.scaleOccupancyUnitCost(
                fixedCost / countVehiclesbyId,
                costByKm,
                oc,
            );
            const costPerSeat = periodUnit / capacity;
            if (!Number.isFinite(costPerSeat)) return '—';
            return this.formatMXNBRL(round2(costPerSeat));
        },
        formatTravelTime(seconds) {
            const n = Number(seconds);
            if (!Number.isFinite(n) || n < 0) return '—';
            const totalMin = Math.round(n / 60);
            const hours = Math.floor(totalMin / 60);
            const minutes = totalMin % 60;
            if (hours > 0) return `${hours}h ${minutes}min`;
            return `${minutes} min`;
        },
        formatScheduleTime(value) {
            if (value == null || value === '') return '—';
            const text = String(value).trim();
            return text || '—';
        },
        /** Gráfico de barras de uma série de custos para o PDF (valores monetários formatados). */
        buildCostBarsChart(labels, values, seriesLabel, color) {
            return {
                kind: 'bars',
                labels,
                maxLabel: this.formatMXNBRL(Math.max(0, ...values)),
                series: [{
                    label: seriesLabel,
                    color,
                    values,
                    valueLabels: values.map((v) => this.formatMXNBRL(v)),
                }],
                legend: [{ label: seriesLabel, color }],
            };
        },
        formatMXNBRL(value) {
            const n = Number(value);
            if (!Number.isFinite(n)) return '—';
            const isEs = this.$i18n && String(this.$i18n.locale).trim() === 'es-MX';
            return n.toLocaleString(
                isEs ? 'es-MX' : 'pt-BR',
                { style: 'currency', currency: isEs ? 'MXN' : 'BRL' },
            );
        },
        formatKm(km) {
            const n = Number(km);
            if (!Number.isFinite(n)) return '—';
            return `${n.toFixed(2)} km`;
        },
        formatPercent(ratio) {
            const n = Number(ratio);
            if (!Number.isFinite(n)) return '—';
            return `${Math.round(n * 100)}%`;
        },
        formatCycleTipoLabel(tipo, dias) {
            const t = String(tipo || 'MENSAL').toUpperCase();
            if (t === 'DIARIO' || t === 'DIÁRIO') {
                return this.$t('routePlanning.sidebar.costCycleDaily');
            }
            if (t === 'SEMANAL') {
                return this.$t('routePlanning.sidebar.costCycleWeekly');
            }
            if (t === 'QUINZENAL') {
                return this.$t('routePlanning.sidebar.costCycleBiweekly');
            }
            if (t === 'DIAS') {
                return this.$t('routePlanning.sidebar.costCycleContractDays', {
                    days: dias != null ? dias : '—',
                });
            }
            return this.$t('routePlanning.sidebar.costCycleMonthly');
        },
        onClickViewControlContract() {
            this.$emit('navigate-control-contract');
        },
        onClickViewOperationalContract(idContrato) {
            this.$emit('navigate-operational-contract', idContrato);
        },
    },
};
</script>

<style scoped>
.roteirizacao-custo-panel {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    padding: 12px 16px 16px;
}
.roteirizacao-custo-panel__empty {
    color: rgba(0, 0, 0, 0.6);
}
.roteirizacao-custo-panel__subtitle-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: -4px;
}
.roteirizacao-custo-panel__subtitle {
    flex: 1 1 auto;
    min-width: 0;
    line-height: 1.3;
    word-break: break-word;
}
.roteirizacao-custo-panel__sequencing {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
    color: rgba(0, 0, 0, 0.6);
}
.roteirizacao-custo-panel__view-contract-btn {
    flex-shrink: 0;
    text-transform: none;
}
.roteirizacao-custo-panel__totals {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.roteirizacao-custo-panel__total-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
}
.roteirizacao-custo-panel__table {
    background: transparent;
}
.roteirizacao-custo-panel__table th {
    font-size: 0.6rem !important;
    white-space: nowrap;
}
.roteirizacao-custo-panel__table td {
    font-size: 0.8rem !important;
}
</style>
